/**
 * Jobs tab component - Spike 13: Job Postings Intelligence
 *
 * Displays hiring metrics, signals, and job postings for a company.
 */

import { useState } from 'react';
import { useCompanyJobs, useHiringMetrics, useHiringSignals } from '../../../hooks/useCompanies';
import type {
  Department,
  HiringVelocity,
  JobListItem,
  DepartmentBreakdownItem,
  HiringSignalItem,
} from '../../../types';

interface JobsTabProps {
  companyId: string;
}

// Verification status badge colors
const verificationColors: Record<string, string> = {
  verified: 'bg-green-100 text-green-800',
  corroborated: 'bg-blue-100 text-blue-800',
  unverified: 'bg-gray-100 text-gray-600',
  stale: 'bg-yellow-100 text-yellow-700',
};

// Velocity indicator colors and icons
const velocityConfig: Record<HiringVelocity, { color: string; icon: string; label: string }> = {
  accelerating: { color: 'text-green-600', icon: '↗', label: 'Accelerating' },
  stable: { color: 'text-gray-600', icon: '→', label: 'Stable' },
  decelerating: { color: 'text-red-600', icon: '↘', label: 'Decelerating' },
};

// Signal severity colors
const severityColors = {
  high: 'bg-red-100 text-red-800 border-red-200',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  low: 'bg-blue-100 text-blue-800 border-blue-200',
};

function MetricCard({
  label,
  value,
  subtitle,
}: {
  label: string;
  value: React.ReactNode;
  subtitle?: string;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="text-sm font-medium text-gray-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-gray-900">{value}</div>
      {subtitle && <div className="mt-1 text-xs text-gray-500">{subtitle}</div>}
    </div>
  );
}

function VelocityIndicator({ velocity }: { velocity: HiringVelocity }) {
  const config = velocityConfig[velocity];
  return (
    <div className={`flex items-center gap-1 ${config.color}`}>
      <span className="text-lg font-bold">{config.icon}</span>
      <span className="font-medium">{config.label}</span>
    </div>
  );
}

function HiringSignalCard({ signal }: { signal: HiringSignalItem }) {
  return (
    <div className={`rounded-lg border p-3 ${severityColors[signal.severity]}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="font-medium capitalize">{signal.signal_type.replace(/_/g, ' ')}</div>
          <div className="mt-1 text-sm opacity-90">{signal.description}</div>
        </div>
        <span className="rounded px-2 py-0.5 text-xs font-medium uppercase">{signal.severity}</span>
      </div>
    </div>
  );
}

function DepartmentBar({ dept, maxJobs }: { dept: DepartmentBreakdownItem; maxJobs: number }) {
  const widthPercent = maxJobs > 0 ? (dept.active_jobs / maxJobs) * 100 : 0;

  return (
    <div className="flex items-center gap-3">
      <div className="w-32 flex-shrink-0">
        <span className="text-sm font-medium text-gray-700">{dept.display_name}</span>
        {dept.is_hot && (
          <span className="ml-1 inline-flex items-center rounded bg-orange-100 px-1.5 py-0.5 text-xs text-orange-700">
            Hot
          </span>
        )}
        {dept.is_cooling && (
          <span className="ml-1 inline-flex items-center rounded bg-blue-100 px-1.5 py-0.5 text-xs text-blue-700">
            Cooling
          </span>
        )}
      </div>
      <div className="flex-1">
        <div className="flex h-6 items-center rounded-full bg-gray-100">
          <div
            className="h-full rounded-full bg-primary-500 transition-all duration-300"
            style={{ width: `${Math.max(widthPercent, 2)}%` }}
          />
        </div>
      </div>
      <div className="w-16 text-right text-sm font-medium text-gray-900">
        {dept.active_jobs} jobs
      </div>
    </div>
  );
}

function JobCard({ job }: { job: JobListItem }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-4 hover:border-gray-300 transition-colors">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h4 className="font-medium text-gray-900 truncate">{job.title}</h4>
          {job.is_remote && (
            <span className="inline-flex items-center rounded bg-purple-100 px-1.5 py-0.5 text-xs text-purple-700">
              Remote
            </span>
          )}
        </div>
        <div className="mt-1 flex items-center gap-3 text-sm text-gray-500">
          {job.department && (
            <span className="capitalize">{job.department.replace(/_/g, ' ')}</span>
          )}
          {job.seniority && (
            <>
              <span className="text-gray-300">|</span>
              <span className="capitalize">{job.seniority}</span>
            </>
          )}
          {job.location && (
            <>
              <span className="text-gray-300">|</span>
              <span>{job.location}</span>
            </>
          )}
        </div>
        {job.salary_range && (
          <div className="mt-1 text-sm font-medium text-green-700">{job.salary_range}</div>
        )}
      </div>
      <div className="ml-4 flex flex-col items-end gap-1">
        <span
          className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${verificationColors[job.verification_status]}`}
        >
          {job.verification_status}
        </span>
        <span className="text-xs text-gray-500">
          {job.days_posted === 0 ? 'Today' : `${job.days_posted}d ago`}
        </span>
        {job.source_count > 1 && (
          <span className="text-xs text-gray-400">{job.source_count} sources</span>
        )}
      </div>
    </div>
  );
}

export function JobsTab({ companyId }: JobsTabProps) {
  const [departmentFilter, setDepartmentFilter] = useState<Department | ''>('');
  const [verificationFilter, setVerificationFilter] = useState<string>('');
  const [page, setPage] = useState(1);

  const { data: metrics, isLoading: metricsLoading } = useHiringMetrics(companyId);
  const { data: signals, isLoading: signalsLoading } = useHiringSignals(companyId);
  const { data: jobsData, isLoading: jobsLoading } = useCompanyJobs(companyId, {
    department: departmentFilter || undefined,
    verification_status: verificationFilter as
      | 'verified'
      | 'corroborated'
      | 'unverified'
      | undefined,
    page,
    page_size: 20,
  });

  const maxDeptJobs = metrics?.departments.reduce((max, d) => Math.max(max, d.active_jobs), 0) || 0;

  return (
    <div className="space-y-8">
      {/* Overview Metrics */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Hiring Overview</h2>
        {metricsLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 animate-pulse">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-24 rounded-lg bg-gray-200" />
            ))}
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <MetricCard
              label="Active Jobs"
              value={metrics?.total_active_jobs || 0}
              subtitle={`${metrics?.verified_job_count || 0} verified`}
            />
            <MetricCard
              label="Hiring Velocity"
              value={<VelocityIndicator velocity={metrics?.hiring_velocity || 'stable'} />}
              subtitle={`${metrics?.net_change_30d || 0 > 0 ? '+' : ''}${metrics?.net_change_30d || 0} in 30d`}
            />
            <MetricCard
              label="Remote Jobs"
              value={`${metrics?.remote_job_percentage?.toFixed(0) || 0}%`}
              subtitle={`of open positions`}
            />
            <MetricCard
              label="Posted (30d)"
              value={metrics?.jobs_posted_30d || 0}
              subtitle={`${metrics?.jobs_removed_30d || 0} removed`}
            />
          </div>
        )}
      </section>

      {/* Hiring Signals */}
      {!signalsLoading && signals && signals.signal_count > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Hiring Signals
            <span className="ml-2 text-sm font-normal text-gray-500">
              ({signals.signal_count} detected)
            </span>
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {signals.signals.map((signal, idx) => (
              <HiringSignalCard key={idx} signal={signal} />
            ))}
          </div>
        </section>
      )}

      {/* Department Breakdown */}
      {metrics && metrics.departments.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">By Department</h2>
          <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
            {metrics.departments
              .sort((a, b) => b.active_jobs - a.active_jobs)
              .map((dept) => (
                <DepartmentBar key={dept.department} dept={dept} maxJobs={maxDeptJobs} />
              ))}
          </div>
        </section>
      )}

      {/* Job Listings */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Job Postings
            {jobsData && (
              <span className="ml-2 text-sm font-normal text-gray-500">
                ({jobsData.total_count} total)
              </span>
            )}
          </h2>

          {/* Filters */}
          <div className="flex items-center gap-3">
            <select
              value={departmentFilter}
              onChange={(e) => {
                setDepartmentFilter(e.target.value as Department | '');
                setPage(1);
              }}
              className="rounded-md border-gray-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="">All Departments</option>
              <option value="engineering">Engineering</option>
              <option value="product">Product</option>
              <option value="data_science">Data Science</option>
              <option value="sales">Sales</option>
              <option value="marketing">Marketing</option>
              <option value="finance">Finance</option>
              <option value="hr">HR</option>
              <option value="operations">Operations</option>
              <option value="customer_success">Customer Success</option>
              <option value="design">Design</option>
              <option value="executive">Executive</option>
            </select>

            <select
              value={verificationFilter}
              onChange={(e) => {
                setVerificationFilter(e.target.value);
                setPage(1);
              }}
              className="rounded-md border-gray-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="">All Status</option>
              <option value="verified">Verified</option>
              <option value="corroborated">Corroborated</option>
              <option value="unverified">Unverified</option>
            </select>
          </div>
        </div>

        {jobsLoading ? (
          <div className="space-y-3 animate-pulse">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 rounded-lg bg-gray-200" />
            ))}
          </div>
        ) : jobsData && jobsData.jobs.length > 0 ? (
          <div className="space-y-3">
            {jobsData.jobs.map((job) => (
              <JobCard key={job.id} job={job} />
            ))}

            {/* Pagination */}
            {jobsData.has_more && (
              <div className="flex justify-center pt-4">
                <button
                  onClick={() => setPage((p) => p + 1)}
                  className="rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                >
                  Load More
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No job postings found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {departmentFilter || verificationFilter
                ? 'Try adjusting your filters'
                : 'No active job postings are being tracked for this company'}
            </p>
          </div>
        )}
      </section>

      {/* Data Source Info */}
      {metrics && (
        <section className="text-sm text-gray-500 border-t border-gray-200 pt-4">
          <div className="flex items-center gap-4">
            {metrics.has_company_careers && (
              <span className="inline-flex items-center">
                <svg
                  className="h-4 w-4 mr-1 text-green-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                Company careers page tracked
              </span>
            )}
            {metrics.salary_coverage_percentage > 0 && (
              <span>Salary data: {metrics.salary_coverage_percentage.toFixed(0)}% coverage</span>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

export default JobsTab;
